import { create } from 'zustand'

/**
 * The player's own position, used to turn "3,2 km del centro" into a distance
 * that is actually from them.
 *
 * WHY IT IS OPT-IN AND CONTEXTUAL. `directoryOrigin()` measures from the
 * centroid of a city's pinned clubs precisely so the app never has to ask for a
 * location permission — a deliberate trade, and the right one for a first run.
 * A permission dialog fired on load, before a stranger knows what the app is,
 * costs you the stranger; browsers also remember a denial, so one badly-timed
 * prompt can burn the capability permanently for that user.
 *
 * So nothing here runs on mount. `request()` is called from a control the
 * player taps, and the centroid stays as the fallback for every other case:
 * not asked, denied, unavailable, timed out.
 *
 * SESSION-ONLY, on purpose. A position is not a preference — it is where
 * somebody physically was at one moment. Persisting it to localStorage would
 * mean showing a player distances from a town they left last week, confidently
 * and with no way for them to tell. The browser re-grants a previously accepted
 * permission without a second prompt, so re-asking next session is cheap.
 */
export type LocationStatus = 'idle' | 'asking' | 'granted' | 'denied' | 'unavailable'

interface PlayerLocationState {
  status: LocationStatus
  position: { lat: number; lng: number } | null
  /** Prompts the browser. Safe to call repeatedly; no-ops while asking. */
  request: () => void
  /** Drop the fix and fall back to the centroid — the player's undo. */
  clear: () => void
}

/** Bounded so a hanging permission dialog cannot leave the UI stuck in `asking`. */
const TIMEOUT_MS = 10_000

export const usePlayerLocation = create<PlayerLocationState>((set, get) => ({
  status: 'idle',
  position: null,
  clear: () => set({ status: 'idle', position: null }),
  request: () => {
    if (get().status === 'asking') return
    // Checked by VALUE, not with `in`: some browsers expose the key while the
    // API is unusable (insecure context, locked-down privacy mode).
    if (typeof navigator === 'undefined' || !navigator.geolocation) {
      set({ status: 'unavailable', position: null })
      return
    }
    set({ status: 'asking' })
    navigator.geolocation.getCurrentPosition(
      (pos) =>
        set({
          status: 'granted',
          position: { lat: pos.coords.latitude, lng: pos.coords.longitude },
        }),
      (err) =>
        // PERMISSION_DENIED (1) is the player's decision and must be remembered
        // as such — never retried automatically, or the app nags. Everything
        // else (position unavailable, timeout) is a technical miss, and the
        // control stays offerable.
        set({
          status: err.code === err.PERMISSION_DENIED ? 'denied' : 'unavailable',
          position: null,
        }),
      { enableHighAccuracy: false, timeout: TIMEOUT_MS, maximumAge: 5 * 60_000 },
    )
  },
}))

/**
 * Which origin the distances on screen were measured from — the single fact the
 * UI needs to label them honestly. A number whose meaning changes silently is
 * the defect this whole area already had once.
 */
export type DistanceOriginKind = 'player' | 'centroid' | 'none'

export function distanceOriginLabel(kind: DistanceOriginKind): string {
  return kind === 'player' ? 'de ti' : 'del centro'
}
